import type { Direction } from "./directionStatus";

/** Break-even epsilon — identical to the open-positions blotter three-way rule. */
const PNL_EPSILON = 0.00000001;

export interface SymbolPnlBadgeProps {
  /** Net open P&L for the symbol, summed across every live ticket. */
  profit: number;
  /**
   * Net direction of the book on this symbol, kept as a tiny ▲/▼ glyph +
   * tooltip so the BUY/SELL affordance isn't lost. Omitted for mixed books.
   */
  direction?: Direction | null;
  className?: string;
}

/**
 * Live per-symbol net P&L pill for the watchlist symbol cell (proposal 21).
 *
 * Replaces the static BUY/SELL badge. Reuses the canonical three-way colour
 * rule and ± formatting from OpenPositionLiveCells verbatim: |x| < ε → muted
 * "0.00", > 0 → blue-400 (price-up), else red-400 (price-down). Same fixed
 * h-[15px] footprint as DirectionStatusCell.
 */
export function SymbolPnlBadge({
  profit,
  direction,
  className,
}: SymbolPnlBadgeProps) {
  const isFlat = Math.abs(profit) < PNL_EPSILON;
  const isProfit = !isFlat && profit > 0;

  const tint = isFlat
    ? "border-border bg-muted/40 text-muted-foreground"
    : isProfit
      ? "border-blue-400/35 bg-blue-400/10 text-blue-400"
      : "border-red-400/35 bg-red-400/10 text-red-400";

  const text = isFlat
    ? profit.toFixed(2)
    : isProfit
      ? `+${profit.toFixed(2)}`
      : profit.toFixed(2);

  const glyph = isFlat ? null : isProfit ? "▲" : "▼";

  const directionLabel =
    direction === "buy" ? "BUY" : direction === "sell" ? "SELL" : "MIXED";
  const title = `${directionLabel} · net P&L ${text}`;

  return (
    <span
      title={title}
      className={`inline-flex h-[15px] min-w-[46px] shrink-0 items-center justify-center gap-[2px] rounded-sm border px-[5px] font-mono text-[9.5px] font-bold tracking-wide tabular-nums ${tint}${
        className ? ` ${className}` : ""
      }`}
    >
      {glyph && <span className="text-[8px] opacity-85">{glyph}</span>}
      {text}
    </span>
  );
}
